import {
  AfterViewInit,
  Component,
  computed,
  DoCheck,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';

import { StateService, TransitionService } from '@uirouter/core';
import * as _ from 'lodash';
import { PresentationUtilsService } from 'src/app/presentations/services/presentation-utils.service';
import { compareIgnoringFields } from 'src/app/shared/helpers/object.helper';
import { createViewportSignal } from 'src/app/shared/helpers/viewport-size.helper';
import { BroadcasterService } from 'src/app/shared/services/broadcaster.service';
import { AutoSaveService } from '../../services/auto-save.service';
import { ComponentsService } from '../../services/components.service';
import { TemplateEditorService } from '../../services/template-editor.service';
import { LayoutMetadataService } from '../../template-components/services/layout-metadata.service';

function createNavbarBehaviorSignal(navbarHeight: () => number) {
  const { height } = createViewportSignal();

  return computed(() => {
    const h = height();
    const nav = navbarHeight();
    const MAX_NAVBAR_HEIGHT_RATIO = 0.25;
    return nav / h > MAX_NAVBAR_HEIGHT_RATIO;
  });
}

export function AutoSaveServiceFactory(
  templateEditorService: TemplateEditorService,
) {
  return new AutoSaveService(
    templateEditorService.save.bind(templateEditorService),
  );
}
@Component({
  selector: 'app-template-editor',
  templateUrl: './template-editor.component.html',
  styleUrls: ['./template-editor.component.scss'],
  providers: [
    {
      provide: AutoSaveService,
      useFactory: AutoSaveServiceFactory,
      deps: [TemplateEditorService],
    },
  ],
  standalone: false,
})
export class TemplateEditorComponent
  implements DoCheck, OnDestroy, OnInit, AfterViewInit
{
  private navbarRef = viewChild('navbar', { read: ElementRef });
  private subscription: any;
  private transitionHook: any;
  private _oldPresentation: any;
  private _debouncedSnapshot = _.debounce(() => {
    this.templateEditorService.snapshotHistory();
  }, 1000);

  private _bypassUnsaved = false;
  private navbarHeight = signal(0);

  isCramped = createNavbarBehaviorSignal(this.navbarHeight);

  constructor(
    private stateService: StateService,
    private transitionService: TransitionService,
    private broadcaster: BroadcasterService,
    public componentsFactory: ComponentsService,
    private templateEditorService: TemplateEditorService,
    private autoSaveService: AutoSaveService,
    private presentationUtils: PresentationUtilsService,
    private layoutMetadataService: LayoutMetadataService,
  ) {
    this.templateEditorService.hasUnsavedChanges = false;

    this.transitionHook = this.transitionService.onStart(
      {
        to: (state: any) => state.name.indexOf('apps.editor.templates') === -1,
      },
      (trans: any) => {
        if (this._bypassUnsaved) {
          this._bypassUnsaved = false;
          return;
        }

        this.autoSaveService.clearSaveTimeout();

        if (
          this.templateEditorService.isUnsaved() &&
          this.templateEditorService.canEditPresentation()
        ) {
          trans.abort();

          this.templateEditorService.save().finally(() => {
            this._bypassUnsaved = true;
            // Use transition.params(), not trans.to().params — the latter is the
            // state's Param declarations (objects), which stringify to
            // `[object Object]` in query params like `?open=`.
            this.stateService.go(trans.to().name, trans.params());
          });
        }
      },
    );

    window.onbeforeunload = (e: Event) => {
      if (this.templateEditorService.isUnsaved()) {
        // Cancel the event
        e.preventDefault(); // If you prevent default behavior in Mozilla Firefox prompt will always be shown
        // Chrome requires returnValue to be set
        e.returnValue = true;
      }
    };

    this.subscription = this.broadcaster.subscribe({
      next: (event: String) => {
        switch (event) {
          case 'presentationCreated':
          case 'presentationUpdated':
          case 'presentationPublished':
            if (!this.autoSaveService.pendingSave) {
              this._setUnsavedChangesAsync(false);
            }
            break;
          case 'presentationDeleted':
            this._setUnsavedChanges(false);
            break;
          case 'risevision.template-editor.presentationLoaded':
          case 'risevision.template-editor.historyRestored':
            this._oldPresentation = _.cloneDeep(
              this.templateEditorService.presentation,
            );
            break;
          case 'risevision.template-editor.brandingUnsavedChanges':
            this.templateEditorService.snapshotHistory();
            this._setUnsavedChangesAsync(true);
            break;
          default:
            return;
        }
      },
    });
  }

  private _checkPresentationChanged() {
    if (!this.templateEditorService.canEditPresentation()) {
      return;
    }

    const ignoredFields = this.presentationUtils.isHtmlPresentation(
      this.templateEditorService.presentation,
    )
      ? TemplateEditorService.IGNORED_FIELDS_HTML_PRESENTATION
      : TemplateEditorService.IGNORED_FIELDS;

    if (_.isEqual(this.templateEditorService.presentation, {})) {
      return;
    } else if (
      !compareIgnoringFields(
        this.templateEditorService.presentation,
        this._oldPresentation,
        ignoredFields,
        true,
      ) ||
      this._hiddenComponentsChanged()
    ) {
      this._oldPresentation = _.cloneDeep(
        this.templateEditorService.presentation,
      );

      const activeEl = document.activeElement;
      const isTypingInSidePanel = !!(
        activeEl &&
        (activeEl instanceof HTMLTextAreaElement ||
          (activeEl instanceof HTMLInputElement &&
            ['text', 'password', 'email', 'search', 'url', ''].includes(
              activeEl.type,
            )))
      );

      if (isTypingInSidePanel) {
        this._debouncedSnapshot();
      } else {
        this._debouncedSnapshot.flush();
        this.templateEditorService.snapshotHistory();
      }

      this.broadcaster.emit(
        'risevision.template-editor.presentationUnsavedChanges',
      );
      this._setUnsavedChangesAsync(true);
    }
  }

  // compareIgnoringFields treats false and undefined as equal, so hiding a
  // component whose customization has no visibility key yet (e.g. a newly
  // added or moved component) would not be detected as a change.
  private _hiddenComponentsChanged(): boolean {
    const getHiddenIds = (presentation) =>
      (presentation?.templateAttributeData?.components || [])
        .filter((component) => component?.customization?.visibility === false)
        .map((component) => component.id);

    return !_.isEqual(
      getHiddenIds(this.templateEditorService.presentation),
      getHiddenIds(this._oldPresentation),
    );
  }

  ngOnInit(): void {
    if (
      !this._oldPresentation &&
      !this.templateEditorService.loadingPresentation
    ) {
      this._oldPresentation = _.cloneDeep(
        this.templateEditorService.presentation,
      );
    }

    void this.templateEditorService.checkIfPresentationIsInteractive();

    // Load the layouts' sizing metadata now, once, so that adding or
    // switching a layout component later is sized from memory rather than
    // waiting on a network request at click time.
    this.layoutMetadataService.preload();
  }

  ngAfterViewInit(): void {
    this.navbarHeight.set(this.navbarRef()?.nativeElement?.offsetHeight ?? 0);
  }

  @HostListener('window:resize')
  onResize() {
    this.navbarHeight.set(this.navbarRef()?.nativeElement?.offsetHeight ?? 0);
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable
    ) {
      return;
    }

    const isMod = event.metaKey || event.ctrlKey;
    if (!isMod) {
      return;
    }

    const key = event.key.toLowerCase();
    const isRedo =
      (isMod && event.shiftKey && key === 'z') ||
      (event.ctrlKey && key === 'y');
    const isUndo = isMod && !event.shiftKey && key === 'z';

    if (isRedo && this.templateEditorService.canRedo()) {
      event.preventDefault();
      this.templateEditorService.redo();
    } else if (isUndo && this.templateEditorService.canUndo()) {
      event.preventDefault();
      this.templateEditorService.undo();
    }
  }

  ngDoCheck(): void {
    if (!this.templateEditorService.presentation || !this._oldPresentation) {
      return;
    }
    this._checkPresentationChanged();
  }

  ngOnDestroy(): void {
    this._debouncedSnapshot.cancel();
    this.subscription.unsubscribe();
    this.transitionHook();
    this.templateEditorService.teardown();

    window.onbeforeunload = undefined;
  }

  considerChromeBarHeight() {
    const userAgent = window.navigator.userAgent;

    // Firefox and Samsung browser require desktop rule
    return (
      this.presentationUtils.isMobileBrowser() &&
      !/Firefox|SamsungBrowser/i.test(userAgent)
    );
  }

  private _setUnsavedChanges(state: any) {
    this.templateEditorService.hasUnsavedChanges = state;

    if (
      this.templateEditorService.hasUnsavedChanges &&
      this.templateEditorService.canEditPresentation()
    ) {
      this.autoSaveService.save();
    }
  }

  private _setUnsavedChangesAsync(state: any) {
    setTimeout(() => {
      this._setUnsavedChanges(state);
    });
  }
}
